import { useEffect } from 'react'
import { Code, Text, TextInput } from '@mantine/core'
import type { TaskArgsComponentProps } from '../args-registry'
import { extractKey } from './UspstfProcessArgs.utils'

/**
 * Args editor/viewer for cpg.uspstf.process: { key? }. Empty = process the bucket's
 * latest corpus import (the normal case — followup runs chained by cpg.uspstf.download
 * carry the explicit key of the file that was just stored); an explicit key replays a
 * specific archived file.
 */
export function UspstfProcessArgs({ value, onChange, readOnly }: TaskArgsComponentProps) {
  const key = extractKey(value)

  // Edit mode opened with no args: seed the empty-object default so the dialog opens
  // valid and the posted run carries explicit args.
  useEffect(() => {
    if (!readOnly && (value === undefined || value === null)) {
      onChange?.({})
    }
  }, [readOnly, value, onChange])

  if (readOnly) {
    return typeof key === 'string' && key ? (
      <Text size="sm">
        Archive: <Code>{key}</Code>
      </Text>
    ) : (
      <Text size="sm">
        <b>Latest import</b> — the most recently archived corpus (default)
      </Text>
    )
  }

  return (
    <TextInput
      label="Archive key"
      description="Full S3 key of an archived corpus file to replay (e.g. uspstf/corpus/uspstf-corpus.2026-07-28T00-16-07Z.json). Leave empty to process the latest import."
      placeholder="latest import"
      value={typeof key === 'string' ? key : ''}
      onChange={(event) => {
        const next = event.currentTarget.value
        onChange?.(next.trim() ? { key: next } : {})
      }}
    />
  )
}
